import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import type {
  AppListResponse,
  AppRow,
  BulkScheduleTarget,
  ConnectionView,
} from "@qlik-scheduler/shared";

import { ApiError, apiRequest } from "../../api/client.js";
import {
  appsKeys,
  useBulkReload,
  useConnections,
  useImmediateReload,
  useSpaces,
} from "../../api/queries.js";
import { Button } from "../../components/Button.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { ErrorAlert } from "../../components/ErrorAlert.js";
import { Skeleton } from "../../components/Skeleton.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import { useToast } from "../../components/Toast.js";
import { BulkScheduleModal } from "../BulkScheduleModal.js";

type ConnectionAppsResult = {
  connection: ConnectionView;
  data: AppListResponse | undefined;
  isLoading: boolean;
  error: Error | null;
};

type AppKey = string; // `${connectionId}:${appId}`
const appKey = (a: { connectionId: number; appId: string }): AppKey =>
  `${a.connectionId}:${a.appId}`;

export function AppsTab({
  onSwitchTab,
}: {
  onSwitchTab?: (next: "apps" | "schedules" | "history" | "active" | "activity") => void;
}) {
  const toast = useToast();
  const connectionsQuery = useConnections();
  const [selectedConnIds, setSelectedConnIds] = useState<number[] | null>(null);
  const [search, setSearch] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [canReloadOnly, setCanReloadOnly] = useState(false);
  // Multi-select state for bulk actions. Keys are `${connectionId}:${appId}`.
  const [selectedApps, setSelectedApps] = useState<Set<AppKey>>(new Set());
  const [bulkReloadConfirmOpen, setBulkReloadConfirmOpen] = useState(false);
  const [bulkScheduleOpen, setBulkScheduleOpen] = useState(false);
  const bulkReload = useBulkReload();

  // Default selection: every active connection. Re-sync once when data first
  // arrives so the user can deselect freely afterwards without us clobbering.
  useEffect(() => {
    if (selectedConnIds === null && connectionsQuery.data) {
      setSelectedConnIds(
        connectionsQuery.data.filter((c) => c.isActive).map((c) => c.id),
      );
    }
  }, [connectionsQuery.data, selectedConnIds]);

  // If the user changes the selected connection(s), drop the space filter —
  // a space id is only meaningful for one specific tenant.
  useEffect(() => {
    setSpaceId("");
  }, [selectedConnIds]);

  const activeConnections = (connectionsQuery.data ?? []).filter((c) => c.isActive);
  const selected = activeConnections.filter((c) => selectedConnIds?.includes(c.id));

  // Fire one /api/apps request per selected connection.
  const queries = useQueries({
    queries: selected.map((c) => ({
      queryKey: appsKeys.list({
        connectionId: c.id,
        search: search || undefined,
        spaceId: spaceId || undefined,
        canReloadOnly: canReloadOnly || undefined,
      }),
      queryFn: ({ signal }: { signal?: AbortSignal }) => {
        const params = new URLSearchParams();
        params.set("connectionId", String(c.id));
        if (search) params.set("search", search);
        if (spaceId) params.set("spaceId", spaceId);
        if (canReloadOnly) params.set("canReloadOnly", "true");
        return apiRequest<AppListResponse>(
          `/api/apps?${params.toString()}`,
          { signal },
        );
      },
    })),
  });

  const perConnection: ConnectionAppsResult[] = selected.map((c, i) => {
    const q = queries[i];
    return {
      connection: c,
      data: q?.data,
      isLoading: q?.isLoading ?? false,
      error: (q?.error ?? null) as Error | null,
    };
  });

  const mergedApps: AppRow[] = useMemo(() => {
    const out: AppRow[] = [];
    for (const pc of perConnection) {
      if (pc.data) out.push(...pc.data.data);
    }
    return out;
  }, [perConnection]);

  // Reset selection when the underlying list changes substantially (filter
  // change or connection toggle), but keep selections of apps that still
  // appear in the new list — preserves intent across small filter tweaks.
  useEffect(() => {
    const visible = new Set(mergedApps.map(appKey));
    setSelectedApps((prev) => {
      let changed = false;
      const next = new Set<AppKey>();
      prev.forEach((k) => {
        if (visible.has(k)) next.add(k);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [mergedApps]);

  const reload = useImmediateReload();

  // Subset of mergedApps that's actually reloadable. Bulk actions skip apps
  // without reload permission rather than 403-ing per item.
  const selectedReloadable = useMemo(
    () =>
      mergedApps.filter(
        (a) => selectedApps.has(appKey(a)) && a.canReload,
      ),
    [mergedApps, selectedApps],
  );

  function toggleApp(a: AppRow) {
    setSelectedApps((prev) => {
      const next = new Set(prev);
      const k = appKey(a);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  function selectAllVisible() {
    setSelectedApps(new Set(mergedApps.filter((a) => a.canReload).map(appKey)));
  }

  function clearSelection() {
    setSelectedApps(new Set());
  }

  async function runBulkReload() {
    if (selectedReloadable.length === 0) return;
    try {
      const res = await bulkReload.mutateAsync({
        items: selectedReloadable.map((a) => ({
          appId: a.appId,
          appName: a.name,
          connectionId: a.connectionId,
          partial: false,
        })),
      });
      if (res.rejectedCount === 0) {
        toast.push(
          "success",
          `Triggered ${res.acceptedCount} reload${res.acceptedCount === 1 ? "" : "s"}.`,
        );
      } else {
        toast.push(
          "info",
          `Triggered ${res.acceptedCount}, ${res.rejectedCount} rejected. See Active Reloads for status.`,
        );
      }
      clearSelection();
      if (onSwitchTab) onSwitchTab("active");
    } catch (err) {
      toast.push(
        "error",
        err instanceof Error ? err.message : "Bulk reload failed",
      );
    } finally {
      setBulkReloadConfirmOpen(false);
    }
  }

  // Targets handed to BulkScheduleModal. Filter to reloadable apps because
  // a schedule against an unreloadable app would just produce auth-failure
  // history rows.
  const bulkScheduleTargets: BulkScheduleTarget[] = useMemo(
    () =>
      selectedReloadable.map((a) => ({
        connectionId: a.connectionId,
        appId: a.appId,
        appName: a.name,
      })),
    [selectedReloadable],
  );

  async function onReloadNow(app: AppRow) {
    try {
      await reload.mutateAsync({
        appId: app.appId,
        connectionId: app.connectionId,
        partial: false,
      });
      toast.push("success", `Reload queued for "${app.name}". See Active Reloads.`);
    } catch (err) {
      toast.push(
        "error",
        err instanceof ApiError ? `${err.code}: ${err.message}` : "Reload failed",
      );
    }
  }

  if (connectionsQuery.isLoading) {
    return (
      <div className="space-y-3" aria-label="Loading connections">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="card p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 space-y-2">
                <Skeleton width="40%" height={14} />
                <Skeleton width="60%" height={12} />
              </div>
              <Skeleton width={110} height={32} rounded="full" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (connectionsQuery.error) {
    return <ErrorAlert>{describeError(connectionsQuery.error)}</ErrorAlert>;
  }
  if (activeConnections.length === 0) {
    return (
      <div className="card empty-surface">
        <p className="text-base font-semibold text-ink">No active connections</p>
        <p>Add one on the Connections page first to browse apps.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter bar — connection chips + search + space + can-reload */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="field-label mr-1">Connections</span>
          {activeConnections.map((c) => {
            const isOn = selectedConnIds?.includes(c.id) ?? false;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={isOn}
                onClick={() => {
                  setSelectedConnIds((cur) => {
                    const base = cur ?? activeConnections.map((x) => x.id);
                    if (base.includes(c.id)) {
                      // Don't allow deselecting the last one — spec: at least one.
                      if (base.length === 1) return base;
                      return base.filter((id) => id !== c.id);
                    }
                    return [...base, c.id];
                  });
                }}
                className={[
                  "chip transition",
                  isOn
                    ? "bg-blue-light text-blue-dark"
                    : "bg-bg text-ink-soft hover:bg-line",
                ].join(" ")}
              >
                {c.connectionName}
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <label htmlFor="apps-search" className="field-label mb-1.5 block">
              Search
            </label>
            <input
              id="apps-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input"
              placeholder="Search app names…"
            />
          </div>
          <SpaceFilter
            selectedConnectionId={
              selected.length === 1 ? selected[0]!.id : null
            }
            value={spaceId}
            onChange={setSpaceId}
            disabledCount={selected.length}
          />

          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={canReloadOnly}
                onChange={(e) => setCanReloadOnly(e.target.checked)}
                className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
              />
              Reload-permission only
            </label>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line bg-bg/70 px-4 py-3 text-[12px] text-ink-soft">
        <div className="flex flex-wrap items-center gap-3">
          <span>
            {mergedApps.length} app{mergedApps.length === 1 ? "" : "s"}
          </span>
          {selectedApps.size > 0 ? (
            <span className="chip bg-blue-light text-blue-dark">
              {selectedApps.size} selected
            </span>
          ) : null}
          <button
            type="button"
            onClick={selectAllVisible}
            className="text-[12px] font-semibold uppercase tracking-[0.06em] text-blue hover:text-blue-dark disabled:opacity-50"
            disabled={
              mergedApps.length === 0 ||
              mergedApps.every((a) => !a.canReload || selectedApps.has(appKey(a)))
            }
          >
            Select all visible
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="text-[12px] font-semibold uppercase tracking-[0.06em] text-ink-soft hover:text-ink disabled:opacity-50"
            disabled={selectedApps.size === 0}
          >
            Clear
          </button>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost-sm"
            onClick={() => setBulkReloadConfirmOpen(true)}
            disabled={selectedReloadable.length === 0 || bulkReload.isPending}
            title={
              selectedReloadable.length === 0
                ? "Select reloadable apps first"
                : `Trigger reload on ${selectedReloadable.length} app(s)`
            }
          >
            {bulkReload.isPending
              ? "Triggering…"
              : `Bulk Reload${selectedReloadable.length ? ` (${selectedReloadable.length})` : ""}`}
          </Button>
          <Button
            variant="ghost-sm"
            onClick={() => setBulkScheduleOpen(true)}
            disabled={selectedReloadable.length === 0}
            title={
              selectedReloadable.length === 0
                ? "Select reloadable apps first"
                : `Create a schedule for ${selectedReloadable.length} app(s)`
            }
          >
            {`Bulk Schedule${selectedReloadable.length ? ` (${selectedReloadable.length})` : ""}`}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={bulkReloadConfirmOpen}
        title="Trigger bulk reload?"
        confirmLabel={`Reload ${selectedReloadable.length} app${selectedReloadable.length === 1 ? "" : "s"}`}
        onCancel={() => setBulkReloadConfirmOpen(false)}
        onConfirm={runBulkReload}
        destructive={false}
        message="This will start an immediate reload on all selected apps. Progress appears in the Active Reloads tab."
      />

      <BulkScheduleModal
        open={bulkScheduleOpen}
        targets={bulkScheduleTargets}
        onClose={() => setBulkScheduleOpen(false)}
        onCreated={(result) => {
          if (result.rejectedCount === 0) {
            toast.push(
              "success",
              `Created ${result.acceptedCount} schedule${result.acceptedCount === 1 ? "" : "s"}.`,
            );
          } else {
            toast.push(
              "info",
              `Created ${result.acceptedCount}, ${result.rejectedCount} rejected.`,
            );
          }
          clearSelection();
          if (onSwitchTab) onSwitchTab("schedules");
        }}
      />

      {/* Per-connection error surface */}
      {perConnection
        .filter((pc) => pc.error)
        .map((pc) => (
          <ErrorAlert key={pc.connection.id} title={`${pc.connection.connectionName} failed`}>
            {describeError(pc.error)}
          </ErrorAlert>
        ))}

      {/* App list */}
      <div className="space-y-2">
        {perConnection.some((pc) => pc.isLoading) ? (
          <div className="space-y-2" aria-label="Loading apps">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="card p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <Skeleton width="35%" height={14} />
                    <Skeleton width="55%" height={11} />
                  </div>
                  <Skeleton width={110} height={32} rounded="full" />
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {mergedApps.length === 0 && !perConnection.some((pc) => pc.isLoading) ? (
          <div className="card empty-surface">
            <p className="text-base font-semibold text-ink">No matching apps</p>
            <p>No apps match the current filters.</p>
          </div>
        ) : null}
        {mergedApps.map((app) => (
          <div
            key={`${app.connectionId}:${app.id}`}
            className="card card-hover flex items-start justify-between gap-4 p-4"
          >
            <label
              className={`flex shrink-0 cursor-pointer items-center pt-0.5 ${app.canReload ? "" : "cursor-not-allowed opacity-40"}`}
              title={
                app.canReload ? "Select for bulk action" : "No reload permission"
              }
            >
              <input
                type="checkbox"
                checked={selectedApps.has(appKey(app))}
                onChange={() => toggleApp(app)}
                disabled={!app.canReload}
                className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
              />
            </label>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-semibold text-ink">
                  {app.name}
                </span>
                {app.isScheduled ? (
                  <StatusBadge tone="blue">Scheduled</StatusBadge>
                ) : null}
                {!app.canReload ? (
                  <StatusBadge tone="grey" title="No reload permission">
                    Read-only
                  </StatusBadge>
                ) : null}
              </div>
              <p className="mt-1 truncate text-[12px] text-ink-soft">
                {/* Lead with the space — end users recognise space names
                    where they don't recognise the connection / owner. When
                    more than one connection is selected, prepend the
                    connection name so apps are still attributable. */}
                {selected.length > 1 ? `${app.connectionName} · ` : ""}
                {app.spaceName ?? "Personal"}
                {app.lastReloadTime
                  ? ` · last reload ${new Date(app.lastReloadTime).toLocaleString()}`
                  : ""}
              </p>
            </div>
            <div className="shrink-0">
              <Button
                variant="primary-sm"
                onClick={() => onReloadNow(app)}
                disabled={!app.canReload || reload.isPending}
                title={app.canReload ? "Reload now" : "No reload permission"}
              >
                {reload.isPending ? "Working…" : "Reload Now"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return "Unknown error";
}

// Space filter dropdown — only meaningful when exactly one connection is
// selected, because space ids are tenant-scoped (no cross-tenant uniqueness).
// When multiple connections are selected, surface a disabled hint instead of
// an unusable text input.
function SpaceFilter({
  selectedConnectionId,
  value,
  onChange,
  disabledCount,
}: {
  selectedConnectionId: number | null;
  value: string;
  onChange: (next: string) => void;
  disabledCount: number;
}) {
  const spacesQuery = useSpaces(selectedConnectionId);

  if (selectedConnectionId === null) {
    return (
      <div>
        <label className="field-label mb-1.5 block">Space</label>
        <div className="input flex items-center bg-bg text-ink-soft">
          {disabledCount === 0
            ? "Pick a connection first"
            : "Pick exactly one connection"}
        </div>
      </div>
    );
  }

  const spaces = spacesQuery.data?.data ?? [];

  return (
    <div>
      <label htmlFor="apps-space" className="field-label mb-1.5 block">
        Space
      </label>
      <select
        id="apps-space"
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={spacesQuery.isLoading || spacesQuery.isError}
      >
        <option value="">
          {spacesQuery.isLoading
            ? "Loading spaces…"
            : spacesQuery.isError
              ? "Could not load spaces"
              : "All spaces"}
        </option>
        {spaces.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    </div>
  );
}
